import { Assistant } from "@inrev/assistant";
import type { AssistantMessageId, Relations } from "@inrev/common";
import { Option } from "@inrev/core";
import { toAssistantContext } from "@portal/api/assistant";
import {
	useCreateAssistantFeedback,
	useFetchAssistantConversation,
	useRunAssistantTurn,
} from "@portal/api/assistant/admin";
import { dockedPanelScrollRegionProps } from "@portal/components/layout/DockedPanel";
import { Button, ClickableIcon, Spinner, Text } from "@portal/components/primitives";
import { useEffect, useMemo, useRef, useState } from "react";
import { match } from "ts-pattern";
import { AdminAssistantCitationPreview } from "./AdminAssistantCitationPreview";
import { AdminAssistantComposer } from "./AdminAssistantComposer";
import { AdminAssistantConversationTitle } from "./AdminAssistantConversationTitle";
import { AdminAssistantMessage, type AssistantTranscriptItem } from "./AdminAssistantMessage";
import { AdminAssistantSuggestions } from "./AdminAssistantSuggestions";
import {
	type AssistantToolCall,
	assistantComposerPlaceholder,
	assistantEmptyCopy,
	assistantFollowUpPrompts,
	assistantNewChatLabel,
	assistantSuggestedPrompts,
} from "./assistantCopy";
import { useAssistantSessionContext } from "./useAssistantSession";
import type { AssistantConversationSelection } from "./useStoredAssistantConversation";

type StreamingToolCall = AssistantToolCall & { id: string };

type StreamingTurn = {
	key: symbol;
	/**
	 * Where the turn was sent. Moves to the created id when a first send creates its conversation, and
	 * is `null` until then if the chat opens another conversation first.
	 */
	conversationId: AssistantConversationSelection | null;
	assistantText: string;
	error: string | null;
	/** Non-null when partial text was streamed and then the turn was cut off. */
	interruptedCode: string | null;
	persistedMessageId: AssistantMessageId | null;
	/** Read off each streamed tool result, as the API reads them to save on the message. */
	sources: string[];
	startedAfterSeq: number;
	toolCalls: StreamingToolCall[];
	userText: string;
};

const applyStreamEvent = (turn: StreamingTurn, event: Assistant.Dtos.Turn.Event): StreamingTurn =>
	match(event)
		.with({ type: "text_delta" }, (delta) => ({
			...turn,
			assistantText: `${turn.assistantText}${delta.text}`,
		}))
		.with({ type: "tool_call" }, (call) =>
			turn.toolCalls.some((tool) => tool.id === call.id)
				? turn
				: {
						...turn,
						toolCalls: [
							...turn.toolCalls,
							{ id: call.id, toolName: call.toolName, isError: false, pending: true },
						],
					},
		)
		.with({ type: "tool_result" }, (result) => ({
			...turn,
			sources: [
				...turn.sources,
				...Assistant.Dtos.Message.webSourceUrls(result.toolName, result.result),
			],
			toolCalls: turn.toolCalls.map((tool) =>
				tool.id === result.id
					? {
							...tool,
							isError: result.isError,
							pending: false,
							status: result.status,
							resultRowCount: result.resultRowCount,
						}
					: tool,
			),
		}))
		.with({ type: "error" }, (error) => ({
			...turn,
			error: error.message,
			interruptedCode: turn.assistantText !== "" ? (error.code ?? "interrupted") : null,
		}))
		.with({ type: "done" }, (done) => ({ ...turn, persistedMessageId: done.messageId }))
		.with({ type: "usage" }, () => turn)
		.exhaustive();

const NEAR_BOTTOM_PX = 80;

const NO_FOLLOW_UPS: string[] = [];

type AdminAssistantToolProps = {
	relation: Relations.Notes;
	conversationId: AssistantConversationSelection;
	/** Called with the created id on the first send, and with `"new"` to start over. */
	onConversationChange: (conversationId: AssistantConversationSelection) => void;
	/** Reports whether starting a new conversation would leave anything behind, for header controls. */
	onCanStartNewChange?: (canStartNew: boolean) => void;
	/**
	 * Bumps when the host opens a conversation, e.g. from its New conversation control. The chat
	 * drops its streamed turn in place rather than remounting, so the composer keeps its draft.
	 */
	openCount?: number;
};

const AssistantChat = ({
	relation,
	conversationId,
	onConversationChange,
	onCanStartNewChange,
	openCount,
}: AdminAssistantToolProps) => {
	// Each turn this chat sent that is still running or failed, so one keeps streaming while another
	// conversation is open and shows again when its own is.
	const [turns, setTurns] = useState<readonly StreamingTurn[]>([]);
	const [shown, setShown] = useState({ openCount, conversationId });
	if (openCount !== shown.openCount || conversationId !== shown.conversationId) {
		setShown({ openCount, conversationId });
		if (openCount !== shown.openCount) {
			// A failure shows once, so leaving its conversation drops it. One that failed behind another
			// conversation waits to be seen. A send still creating its conversation lets go of the blank
			// chat left behind, and shows in its conversation once that exists.
			setTurns((current) =>
				current
					.filter(
						(turn) =>
							turn.error === null ||
							(turn.conversationId !== null &&
								turn.conversationId !== "new" &&
								turn.conversationId !== shown.conversationId),
					)
					.map((turn) =>
						turn.conversationId === "new" ? { ...turn, conversationId: null } : turn,
					),
			);
		}
	}
	const streaming = turns.find((turn) => turn.conversationId === conversationId) ?? null;
	const sendAbortsRef = useRef(new Set<AbortController>());
	const transcriptRef = useRef<HTMLDivElement>(null);
	const stickToBottomRef = useRef(true);
	const openCountRef = useRef(openCount);

	const { runAssistantTurn, isAssistantTurnStreaming } = useRunAssistantTurn();
	const openId = conversationId === "new" ? undefined : conversationId;
	// A refetch mid-stream would show the saved question beside the streaming one.
	const { conversation, conversationIsLoading, conversationError } = useFetchAssistantConversation(
		openId,
		{ pollForTitle: !isAssistantTurnStreaming(openId) },
	);
	const { createAssistantFeedback } = useCreateAssistantFeedback();
	// A gone conversation (null) reads as a blank one: the first send creates its replacement.
	const liveId = conversation === null ? undefined : openId;
	// Only the open conversation's turn locks the chat. Others run on behind it. Until a first send's
	// conversation exists, only this chat's own turn says whether it is still running.
	const isSending =
		liveId === undefined
			? streaming !== null && streaming.error === null
			: isAssistantTurnStreaming(liveId);

	useEffect(() => {
		const sendAborts = sendAbortsRef.current;
		return () => {
			for (const abort of sendAborts) abort.abort();
		};
	}, []);

	// Before the scroll effect, so a conversation opened from history starts at its latest answer.
	useEffect(() => {
		openCountRef.current = openCount;
		stickToBottomRef.current = true;
	}, [openCount]);

	const onTranscriptScroll = () => {
		const node = transcriptRef.current;
		if (node === null) return;
		stickToBottomRef.current =
			node.scrollHeight - node.scrollTop - node.clientHeight < NEAR_BOTTOM_PX;
	};

	useEffect(() => {
		const node = transcriptRef.current;
		if (node === null || !stickToBottomRef.current) return;
		node.scrollTop = node.scrollHeight;
	}, [conversation?.messages, streaming]);

	// A hidden transcript ignores scrolling, so text streamed while its host showed something else
	// is caught up once the transcript has a size again.
	useEffect(() => {
		const node = transcriptRef.current;
		if (node === null) return;
		const observer = new ResizeObserver(() => {
			if (stickToBottomRef.current) node.scrollTop = node.scrollHeight;
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	const send = async (message: string) => {
		if (isSending) return;
		const key = Symbol("assistant-turn");
		const updateTurn = (update: (turn: StreamingTurn) => StreamingTurn) =>
			setTurns((current) => current.map((turn) => (turn.key === key ? update(turn) : turn)));
		const abort = new AbortController();
		sendAbortsRef.current.add(abort);
		setTurns((current) => [
			...current.filter((turn) => turn.conversationId !== conversationId),
			{
				key,
				conversationId,
				assistantText: "",
				error: null,
				interruptedCode: null,
				persistedMessageId: null,
				sources: [],
				startedAfterSeq: conversation?.messages.at(-1)?.seq ?? 0,
				toolCalls: [],
				userText: message,
			},
		]);
		stickToBottomRef.current = true;
		const sentAtOpenCount = openCount;
		try {
			await runAssistantTurn({
				conversation:
					liveId === undefined ? { create: toAssistantContext(relation) } : { id: liveId },
				message,
				onEvent: (event) => updateTurn((turn) => applyStreamEvent(turn, event)),
				// Opening another conversation mid-create must not be pulled back to this one.
				onCreated: (createdId) => {
					updateTurn((turn) => ({ ...turn, conversationId: createdId }));
					if (openCountRef.current === sentAtOpenCount) onConversationChange(createdId);
				},
				signal: abort.signal,
			});
		} catch (error) {
			updateTurn((turn) => ({
				...turn,
				error: error instanceof Error ? error.message : "Turn failed",
				interruptedCode: turn.assistantText === "" ? null : (turn.interruptedCode ?? "turn_failed"),
			}));
			return;
		} finally {
			sendAbortsRef.current.delete(abort);
		}
		setTurns((current) => current.filter((turn) => turn.key !== key || turn.error !== null));
	};

	// Apart from the streamed turn, so each token re-renders only the rows it changes.
	const persisted: AssistantTranscriptItem[] = useMemo(
		() =>
			conversation?.messages.map((message) => ({
				feedback: message.feedback,
				feedbackMessageId: message.role === "assistant" ? message.id : undefined,
				interruptedCode: message.role === "assistant" ? message.interruptedCode : undefined,
				role: message.role,
				sources: message.content.sources,
				text: message.content.text,
				toolCalls: message.toolCalls.map((tool) => ({
					toolName: tool.toolName,
					isError: tool.isError,
					status: tool.status,
					resultRowCount: tool.resultRowCount,
					durationMs: tool.durationMs,
				})),
			})) ?? [],
		[conversation?.messages],
	);

	const items: AssistantTranscriptItem[] = useMemo(() => {
		if (streaming === null) return persisted;
		const settled =
			conversation?.messages.some(
				(message) =>
					(message.id === streaming.persistedMessageId && streaming.persistedMessageId !== null) ||
					(message.role === "assistant" &&
						message.seq > streaming.startedAfterSeq &&
						message.interruptedCode !== null),
			) === true;
		if (settled) return persisted;
		return [
			...persisted,
			{ role: "user" as const, text: streaming.userText },
			{
				role: "assistant" as const,
				sources: streaming.sources,
				text: streaming.assistantText,
				toolCalls: streaming.toolCalls,
				interruptedCode: streaming.interruptedCode,
			},
		];
	}, [conversation?.messages, persisted, streaming]);

	const showSetupError = conversation === undefined && conversationError !== null;
	const suggested = assistantSuggestedPrompts(relation);

	const firstUserText = items.find((item) => item.role === "user")?.text;
	// The streamed question names a new conversation until the server's fallback arrives.
	const conversationTitle = Option.first([
		conversation?.title,
		conversation?.fallbackTitle,
		Option.flatMap(firstUserText, Assistant.Text.conversationFallbackTitle),
	]);
	const askedPrompts = useMemo(
		() =>
			new Set(
				items.filter((item) => item.role === "user").map((item) => item.text.trim().toLowerCase()),
			),
		[items],
	);
	const followUpPrompts = useMemo(
		() =>
			assistantFollowUpPrompts(relation).filter(
				(prompt) => !askedPrompts.has(prompt.trim().toLowerCase()),
			),
		[askedPrompts, relation],
	);

	const resetConversation = () => {
		setTurns((current) => current.filter((turn) => turn.conversationId !== conversationId));
		onConversationChange("new");
	};

	const composerDisabled = conversationIsLoading || isSending || showSetupError;
	const hasTranscript = items.length > 0;
	const canStartNew = hasTranscript && !isSending;

	const startNewChat = () => {
		if (canStartNew) resetConversation();
	};

	useEffect(() => {
		onCanStartNewChange?.(canStartNew);
		return () => onCanStartNewChange?.(false);
	}, [canStartNew, onCanStartNewChange]);

	return (
		<AdminAssistantCitationPreview>
			<div className="flex h-full min-h-0 flex-col" data-testid="assistant-tool">
				<div
					className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-3.5 py-4"
					data-testid="assistant-transcript"
					onScroll={onTranscriptScroll}
					ref={transcriptRef}
					{...dockedPanelScrollRegionProps}
				>
					{showSetupError ? (
						<div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
							<Text color="danger" type="body">
								{conversationError?.message}
							</Text>
							<Button
								color="secondary"
								data-testid="assistant-retry"
								onClick={resetConversation}
								size="sm"
							>
								Retry
							</Button>
						</div>
					) : conversationIsLoading ? (
						<div className="flex h-full items-center justify-center">
							<Spinner />
						</div>
					) : !hasTranscript ? (
						<div className="flex h-full flex-col items-center justify-center gap-3 px-2 text-center">
							<Text color="secondary" type="body">
								{assistantEmptyCopy(relation)}
							</Text>
							<AdminAssistantSuggestions
								className="flex flex-col items-center gap-1.5"
								onSelect={(prompt) => void send(prompt)}
								prompts={suggested}
								testId="assistant-empty-suggestions"
							/>
						</div>
					) : (
						<div className="flex flex-col gap-4">
							{Option.isSome(conversationTitle) && (
								<AdminAssistantConversationTitle
									lastActivityAt={conversation?.lastActivityAt}
									title={conversationTitle}
								/>
							)}
							{items.map((item, index) => {
								const previous = items[index - 1];
								const precedingUserText =
									item.role === "assistant" &&
									Option.isSome(item.interruptedCode) &&
									previous?.role === "user"
										? previous.text
										: undefined;
								return (
									<AdminAssistantMessage
										conversationId={liveId}
										item={item}
										key={`${item.role}-${index}`}
										onFeedback={createAssistantFeedback}
										onRetry={Option.mapOrElse(
											precedingUserText,
											(text) => () => void send(text),
											undefined,
										)}
										streaming={isSending && index === items.length - 1 && item.role === "assistant"}
									/>
								);
							})}
							{streaming !== null &&
								streaming.error !== null &&
								streaming.interruptedCode === null && (
									<Text color="danger" data-testid="assistant-error" type="caption">
										{streaming.error}
									</Text>
								)}
						</div>
					)}
				</div>
				<AdminAssistantComposer
					disabled={composerDisabled}
					focusKey={openCount}
					followUpPrompts={hasTranscript ? followUpPrompts : NO_FOLLOW_UPS}
					onClear={startNewChat}
					onSend={(message) => void send(message)}
					placeholder={assistantComposerPlaceholder(relation)}
				/>
			</div>
		</AdminAssistantCitationPreview>
	);
};

/** Remounts per relation, so a switch drops the old relation's transcript and aborts its turn. */
export const AdminAssistantTool = (props: AdminAssistantToolProps) => (
	<AssistantChat key={`${props.relation.relationType}.${props.relation.relationId}`} {...props} />
);

/** The tools rail on bond and bond request pages. Reads its conversation from `AssistantSessionProvider`. */
export const AdminAssistantRailTool = ({ relation }: { relation: Relations.Notes }) => {
	const { conversationId, openCount, setConversationId, setCanStartNew } =
		useAssistantSessionContext();
	return (
		<AdminAssistantTool
			conversationId={conversationId}
			onCanStartNewChange={setCanStartNew}
			onConversationChange={setConversationId}
			openCount={openCount}
			relation={relation}
		/>
	);
};

/** New conversation in the tools rail's header. */
export const AdminAssistantRailHeaderActions = () => {
	const { canStartNew, openConversation } = useAssistantSessionContext();
	return (
		<ClickableIcon
			aria-label={assistantNewChatLabel}
			color="tertiary"
			disabled={!canStartNew}
			onClick={() => openConversation("new")}
			size="mini"
			testId="assistant-new-chat"
			tooltip={assistantNewChatLabel}
			trackingId="assistant-rail-new-conversation"
			type="pencil-square"
		/>
	);
};
